import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { MessageSquare, Search, Trash2, User, Building } from 'lucide-react';

const AdminMessages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, message: null });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchMessages();
  }, [user, navigate, page]);

  const fetchMessages = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('skip', page * 50);
      params.append('limit', 50);

      const res = await api.get(`/admin/messages?${params.toString()}`);
      setMessages(res.data.messages);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.message) return;
    try {
      await api.delete(`/admin/messages/${deleteModal.message.id}`);
      toast.success('Message deleted');
      setDeleteModal({ open: false, message: null });
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  return (
    <AdminLayout title="Messages" breadcrumbs={[{ label: 'Messages' }]}>
      <div className="space-y-6">
        {/* Search */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <Input
                placeholder="Search by sender or recipient..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyUp={(e) => e.key === 'Enter' && fetchMessages()}
                className="pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
              />
            </div>
            <Button onClick={fetchMessages} className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]">
              Search
            </Button>
          </div>
        </Card>

        {/* Messages Table */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F8FF]/50">
                <tr>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Sender</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Recipient</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Content</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Date</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                  <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF5]">
                {messages.map((message) => (
                  <tr key={message.id} className="hover:bg-[#F5F8FF]/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Building className="w-4 h-4 text-blue-400" />
                        <span className="text-[#0A0F1E]">{message.sender_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-[#1B5AFF]" />
                        <span className="text-[#0A0F1E]">{message.recipient_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-[#0A0F1E] text-sm max-w-xs truncate">
                        {message.content}
                      </p>
                    </td>
                    <td className="p-4 text-[#5B6472] text-sm">
                      {message.created_at ? new Date(message.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        message.read ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {message.read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                    <td className="p-4">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-400 hover:text-red-300"
                        onClick={() => setDeleteModal({ open: true, message })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {messages.length === 0 && !loading && (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
              <p className="text-[#5B6472]">No messages found</p>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
            <p className="text-[#5B6472] text-sm">Total: {total} messages</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#E8EDF5] text-[#0A0F1E]">Previous</Button>
              <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total} className="border-[#E8EDF5] text-[#0A0F1E]">Next</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, message: null })}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Message</DialogTitle>
          </DialogHeader>
          <p className="text-[#5B6472]">
            Are you sure you want to delete this message thread between {deleteModal.message?.sender_name} and {deleteModal.message?.recipient_name}?
            <strong className="text-red-400"> This action cannot be undone.</strong>
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteModal({ open: false, message: null })} className="text-[#5B6472]">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF]">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminMessages;
